import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, ButtonBase, Collapse, Divider, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { Dot } from "Components/_Shared/Dot";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useState } from "react";
import { type NsureSelectionItem, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { InvestmentsNsureSelectionHeader } from "./InvestmentsNsureSelectionHeader";
import { useInstrumentTypeLabel } from "./useInstrumentTypeLabel";
import { formatSrriSri } from "./utils";

type Props = {
  readonly selection: NsureSelectionItem;
};

type CompositionRowProps = {
  readonly name: string;
  readonly weight: number | null;
  readonly meta?: ReactNode;
  readonly action?: ReactNode;
};

const CompositionRow = ({ name, weight, meta, action }: CompositionRowProps) => (
  <Flex px={Spacing.XL} py={Spacing.M} alignItems="center" gap={Spacing.M} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
    <Stack flex={1} minWidth={0} spacing={Spacing.XS}>
      <Typography
        variant="body2"
        title={name}
        sx={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", overflowWrap: "anywhere" }}
      >
        {name}
      </Typography>
      {meta && (
        <HStack alignItems="center" gap={Spacing.M}>
          {meta}
        </HStack>
      )}
    </Stack>

    <HStack alignItems="center" gap={Spacing.S} flexShrink={0}>
      <LinearProgress
        variant="determinate"
        value={Math.min(Math.max(weight ?? 0, 0), 100)}
        sx={{
          width: 40,
          height: 4,
          borderRadius: 2,
          bgcolor: "grey.100",
          "& .MuiLinearProgress-bar": { borderRadius: 2 },
        }}
      />
      <Typography variant="body2" fontWeight={600} width={40} textAlign="right">
        {weight ?? "—"} %
      </Typography>
      {action}
    </HStack>
  </Flex>
);

export const InvestmentsNsureSelectionCard = ({ selection }: Props) => {
  const { t } = useResource();
  const getInstrumentTypeLabel = useInstrumentTypeLabel();
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const isPortfolio = selection.type === NsureSelectionItemType.Portfolio;
  const compositionCount = isPortfolio ? (selection.strategyInstruments?.length ?? 0) : (selection.instrumentRelations?.length ?? 0);

  return (
    <Box border="1px solid" borderColor="divider" borderRadius={Radius.L} bgcolor="white" overflow="hidden">
      <Box px={Spacing.XL} py={Spacing.L}>
        <InvestmentsNsureSelectionHeader selection={selection} />
      </Box>

      {compositionCount > 0 && (
        <Box borderTop="1px solid" borderColor="divider">
          <ButtonBase
            onClick={() => setDetailsExpanded((prev) => !prev)}
            aria-expanded={detailsExpanded}
            aria-label={t(
              detailsExpanded
                ? Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.CollapseComposition
                : Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.ExpandComposition,
            )}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              px: Spacing.XL,
              py: Spacing.M,
              "&:hover": { bgcolor: "grey.50" },
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.StrategyComposition)}
            </Typography>
            <HStack alignItems="center" gap={Spacing.M}>
              <Flex alignItems="center" justifyContent="center" minWidth={20} px={Spacing.XS} borderRadius={Radius.MAX} bgcolor="grey.100">
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {compositionCount}
                </Typography>
              </Flex>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  color: "text.secondary",
                  transition: "transform 0.2s",
                  transform: detailsExpanded ? "rotate(180deg)" : "none",
                }}
              />
            </HStack>
          </ButtonBase>

          <Collapse in={detailsExpanded}>
            <Divider />
            <Stack pb={Spacing.M} divider={<Divider />}>
              {isPortfolio
                ? selection.strategyInstruments?.map((instrument) => (
                    <CompositionRow key={instrument.id} name={instrument.name ?? "—"} weight={instrument.participationInPercentage} />
                  ))
                : selection.instrumentRelations?.map((rel) => (
                    <CompositionRow
                      key={rel.instrumentRelationID}
                      name={rel.instrument?.name ?? "—"}
                      weight={rel.weight ?? null}
                      meta={
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.SrriSri)}{" "}
                            {formatSrriSri(rel.instrument?.srri ?? null, rel.instrument?.sri ?? null)}
                          </Typography>
                          {rel.instrument?.instrumentType && (
                            <>
                              <Dot />
                              <Typography variant="caption" color="text.secondary">
                                {getInstrumentTypeLabel(rel.instrument.instrumentType)}
                              </Typography>
                            </>
                          )}
                        </>
                      }
                      action={
                        <Tooltip
                          arrow
                          title={
                            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: Spacing.XS, columnGap: Spacing.L }}>
                              <Typography variant="caption" color="grey.300">
                                {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Isin)}
                              </Typography>
                              <Typography variant="caption" color="grey.50">
                                {rel.instrument?.isin ?? "—"}
                              </Typography>
                              <Typography variant="caption" color="grey.300">
                                {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Symbol)}
                              </Typography>
                              <Typography variant="caption" color="grey.50">
                                {rel.instrument?.ticker ?? "—"}
                              </Typography>
                            </Box>
                          }
                        >
                          <IconButton
                            size="small"
                            sx={{ flexShrink: 0, p: Spacing.XS }}
                            aria-label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.InstrumentInfo)}
                          >
                            <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
